import isEqual from 'lodash/isEqual';
import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';

import { DATE_FORMAT, NUMERIC_TYPE } from 'static/Constants';
import {
  checkArrayOfPrimitiveValues,
  getFormValues,
  invalidValues,
  isEmptyArray,
  isEmptyObject,
  isObject,
  lowercaseProductData,
} from '../utils';

import { getDataByFieldFullPath } from 'pages/product-full-view/shared/utils';

export const getHeaderModules = (headerName, isProductOverlay) => {
  return isProductOverlay ? `${headerName} - Overlay` : headerName;
};

export const checkIsInvalidValueOverlay = (value) => {
  return (
    invalidValues.includes(value) ||
    isEmptyArray(value) ||
    (isObject(value) && isEmptyObject(value))
  );
};

export const getOriginalDataFormProduct = (
  editedModule = {},
  dataProductModules = {}
) => {
  const foundDataProductModule = dataProductModules?.productItemModules?.find(
    (item) => item?.moduleName === editedModule?.moduleName
  );

  const formValues = getFormValues(
    lowercaseProductData(foundDataProductModule?.data),
    editedModule?.moduleProperties
  );

  return formValues;
};

export const checkDiffProductValueOverlay = ({
  property,
  originalValue: originalValueProp,
  valueOverlay: valueOverlayProp,
}) => {
  const { propertyType, isArray } = property;
  const originalValue = originalValueProp ?? null;
  const valueOverlay = valueOverlayProp ?? null;

  if (isArray) {
    return !isEqual(originalValue, valueOverlay);
  }

  if (propertyType === 'string') {
    return originalValue?.toLowerCase() !== valueOverlay?.toLowerCase();
  }

  if (NUMERIC_TYPE.includes(propertyType)) {
    return originalValue !== valueOverlay;
  }

  if (propertyType === 'datetime') {
    const formatOriginalValue = originalValue?.format(
      DATE_FORMAT.SYSTEM_FORMAT
    );
    const formatValueOverlay = valueOverlay?.format(DATE_FORMAT.SYSTEM_FORMAT);

    return formatOriginalValue !== formatValueOverlay;
  }

  if (propertyType === 'boolean') {
    return originalValue !== valueOverlay;
  }

  return false;
};

export const transformDataProductOverlay = ({
  dataProduct,
  fieldFullPath,
  isDatetime,
}) => {
  let value = getDataByFieldFullPath(dataProduct, fieldFullPath);

  if (isDatetime && !value) {
    value = null;
  }

  return {
    ...dataProduct,
    [fieldFullPath]: value,
  };
};

export const generateOriginalNestedIdxFormElement = ({
  parentNestedIndex = [],
  propertyName,
  field,
}) => {
  return parentNestedIndex.concat([propertyName, field.fieldKey]);
};

export const generateOriginalStringPath = (indexesField) => {
  let result = '';

  if (indexesField) {
    indexesField.forEach((field, index) => {
      if (typeof field === 'number') {
        result = result + `[${field}]`;
      } else {
        if (index === 0) {
          result = result + field;
        } else {
          result = result + '.' + field;
        }
      }
    });
  }

  return result;
};

export const addModuleNameToDeleteFields = ({ deletedFields, moduleName }) => {
  return deletedFields.map((field) => `${moduleName}.${field}`);
};

export const removeModuleNameFromDeleteFields = ({ deletedFields }) => {
  if (!deletedFields) return [];

  let result = [];

  deletedFields.forEach((field) => {
    const splittedFields = field.split('.');
    splittedFields.shift();
    const removedField = splittedFields.join('.');
    result.push(removedField);
  });

  return result;
};

export const transformPathIndexesField = (path) => {
  const keys = path.split('.'); // Split the path by period
  const result = [];

  keys.forEach((key) => {
    const match = key.match(/\w+|\[\d+\]/g); // Match word characters and array indices

    if (match) {
      match.forEach((part) => {
        if (part.startsWith('[')) {
          // Extract the index from array indices and convert it to a number
          result.push(parseInt(part.slice(1, -1)));
        } else {
          result.push(part);
        }
      });
    }
  });

  return result;
};

export const checkHiddenFormField = ({
  parentNestedIndex,
  field,
  propertyName,
  deletedFieldsOverlay,
}) => {
  const fullPath = generateOriginalNestedIdxFormElement({
    parentNestedIndex,
    field,
    propertyName,
  });
  const stringPath = generateOriginalStringPath(fullPath);

  return deletedFieldsOverlay.includes(stringPath);
};

const markFilteredFieldAsNullableValue = ({ data, deletedFields }) => {
  let result = cloneDeep(data);

  deletedFields.forEach((indexField) => {
    result = set(result, indexField, null);
  });

  return result;
};

const removeNullableValues = (data) => {
  let result = {};

  for (const [key, values] of Object.entries(data)) {
    if (Array.isArray(values)) {
      const filteredValues = values.filter(
        (value) => value !== null && value !== undefined
      );
      const isArrayPrimitiveTypes = checkArrayOfPrimitiveValues(filteredValues);

      if (isArrayPrimitiveTypes) {
        result = {
          ...result,
          [key]: filteredValues,
        };
      } else {
        result = {
          ...result,
          [key]: filteredValues.map((value) => removeNullableValues(value)),
        };
      }
    } else {
      result = {
        ...result,
        [key]: values,
      };
    }
  }
  return result;
};

export const filterDataProductByDeletedField = ({ data, deletedFields }) => {
  if (!deletedFields || deletedFields?.length === 0) return data;

  const markedNullableData = markFilteredFieldAsNullableValue({
    data,
    deletedFields,
  });

  return removeNullableValues(markedNullableData);
};

/* 
"Actually, the array initially contains only primitive values, but when synchronized with data, it can also include empty objects."
*/
export const filterInvalidValues = (values) => {
  return values.filter((value) => {
    if (value && typeof value === 'object') {
      return !isEmptyObject(value);
    }
    return !invalidValues.includes(value);
  });
};

export const reindexedFields = ({
  fields,
  propertyName,
  parentNestedIndex,
  deletedFields,
}) => {
  let result = [];

  fields.forEach((field) => {
    const idxOriginalFormElement = generateOriginalNestedIdxFormElement({
      parentNestedIndex,
      propertyName,
      field,
    });

    const stringPath = generateOriginalStringPath(idxOriginalFormElement);

    if (!deletedFields.includes(stringPath)) {
      result.push(field);
    }
  });

  return result;
};

// "Parent1.Parent2.Child1" -> Child1
export const removeParentPathFromChildPath = ({ childPath, parentPath }) => {
  let result = [];

  childPath.forEach((path) => {
    const newPath = path.split(`${parentPath}.`).pop();
    result.push(newPath);
  });

  return result;
};
